import { useState, useEffect, useCallback } from 'react';
import gun from '../config/gun.config';
import { track } from '../analytics';

/** Follow system: stabeon_follows/{myId}/{targetId} and reverse stabeon_followers/{targetId}/{myId} */

export function useFollows(myId: string | null) {
  const [following, setFollowing] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!myId) return;
    const node = gun.get('stabeon_follows').get(myId);
    const mapNode = node.map();
    mapNode.on((val: any, key: string) => {
      if (val) {
        setFollowing(prev => new Set([...prev, key]));
      } else {
        setFollowing(prev => new Set([...prev].filter(id => id !== key)));
      }
    });
    return () => { try { mapNode.off(); } catch {} };
  }, [myId]);

  const follow = useCallback((targetId: string) => {
    if (!myId) return;
    const ts = Date.now();
    gun.get('stabeon_follows').get(myId).get(targetId).put(ts as any);
    gun.get('stabeon_followers').get(targetId).get(myId).put(ts as any);
    track('user_followed');
    setFollowing(prev => new Set([...prev, targetId]));
  }, [myId]);

  const unfollow = useCallback((targetId: string) => {
    if (!myId) return;
    gun.get('stabeon_follows').get(myId).get(targetId).put(null as any);
    gun.get('stabeon_followers').get(targetId).get(myId).put(null as any);
    setFollowing(prev => new Set([...prev].filter(id => id !== targetId)));
  }, [myId]);

  const isFollowing = useCallback((targetId: string) => following.has(targetId), [following]);

  return { following, isFollowing, follow, unfollow };
}

export function useFollowerCount(userId: string | null): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!userId) return;
    const followers = new Set<string>();
    const node = gun.get('stabeon_followers').get(userId);
    const mapNode = node.map();
    mapNode.on((val: any, key: string) => {
      if (val) followers.add(key); else followers.delete(key);
      setCount(followers.size);
    });
    return () => { try { mapNode.off(); } catch {} };
  }, [userId]);
  return count;
}
